/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import {
  Button,
  Col,
  List,
  Row,
  Skeleton,
  Tag,
  Popconfirm,
  Modal,
  InputNumber,
  Select,
  Form,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";

import { CircleDollarSign, X, MapPin, ArrowRightLeft } from "lucide-react";

import useExcursions from "../hooks/useExcursions";
import usePassengers from "../hooks/usePassengers";
import useParseString from "../hooks/useParseString";

import ExcursionBusesExpandableDetail from "./ExcursionBusesExpandableDetail";
import BusPaymentFormModal from "./BusPaymentFormModal";
import PassengerForm from "./PassengerForm";
import useAuth from "../hooks/useAuth";

const parseExpenseType = {
  GUIDE_PAYMENT: "Pago a Guía",
  TRANSPORT_PAYMENT: "Pago a Transportista",
  FIXED_COST_PER_PASSENGER: "Costo Fijo por Pasajero",
  ADDITIONAL_EXPENSES: "Gastos Adicionales",
};

// Orden de zonas
const ZONE_ORDER = ["Perla", "Centro", "Aldrey"];

const getZoneIndex = (zones) => {
  if (!zones) return 999;
  const index = ZONE_ORDER.findIndex(z => zones.toLowerCase().includes(z.toLowerCase()));
  return index === -1 ? 999 : index;
};

const CLIENT_URL =
  process.env.REACT_APP_CLIENT_URL || "https://caturismo.online";

function ExcursionBusesExpandable({
  record,
  selectedBus,
  selectedExcursion,
  updateBus,
  initialPassengers,
}) {
  const {
    updatePassenger,
    passengers,
    getPassengers,
    setPassengers,
    loading,
    createPayment,
    getPaymentMethods,
    paymentMethods,
  } = usePassengers();

  const { getUser } = useAuth();

  const { deleteBusPayment, updateBusPayment, createBusPayment } =
    useExcursions();

  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [hotelItems, setHotelItems] = useState([]);
  const [mapSrc, setMapSrc] = useState("");
  const [selectedHotelFilter, setSelectedHotelFilter] = useState(null);
  const [user, setUser] = useState(getUser());

  const { formatCurrency } = useParseString();

  const [isBusPaymentModalOpen, setIsBusPaymentModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPassengerModalOpen, setIsPassengerModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isMovePassengerModalOpen, setIsMovePassengerModalOpen] = useState(false);
  const [selectedPassengerId, setSelectedPassengerId] = useState(null);
  const [passengerToMove, setPassengerToMove] = useState(null);
  const [currentPassenger, setCurrentPassenger] = useState(null);
  const [paymentForm] = Form.useForm();

  useEffect(() => {
    setPassengers(initialPassengers);
  }, [initialPassengers]);

  // Auto-refresh cada 5 segundos para recargar información de pasajeros
  useEffect(() => {
    if (!selectedBus?.id) return;

    const refreshInterval = setInterval(() => {
      getPassengers({ scheduled_excursion_bus__id: selectedBus.id });
      updateBus();
    }, 5000); // 5 segundos

    return () => clearInterval(refreshInterval);
  }, [selectedBus?.id]);

  // Actualizar lista de hoteles cuando cambian los datos del bus
  useEffect(() => {
    if (!selectedBus) return;

    const hotels = selectedBus.passengersHotels
      .map((passengerHotel) => ({
        id: passengerHotel.hotel.id,
        name: passengerHotel.hotel.name,
        zones: passengerHotel.hotel.zones,
        seats: passengerHotel.seats,
      }))
      .sort((a, b) => getZoneIndex(a.zones) - getZoneIndex(b.zones));

    setHotelItems(hotels);
  }, [selectedBus]);

  // Solo resetear filtro cuando cambia el ID del bus (se expande otro micro)
  useEffect(() => {
    setSelectedHotelFilter(null);
  }, [selectedBus?.id]);

  useEffect(() => {
    if (!selectedBus) return;

    if (!selectedBus.passengersHotels?.length) {
      setMapSrc(
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53902.66485719307!2d-57.589060716232815!3d-38.0032061771207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9584d94d19d34209%3A0xdd9670804bfed126!2sMar%20del%20Plata%2C%20Provincia%20de%20Buenos%20Aires!5e0!3m2!1ses-419!2sar!4v1735425233832!5m2!1ses-419!2sar"
      );
      return;
    }

    const coords = selectedBus.passengersHotels.map(
      (ph) => ph.hotel.coordinates
    );

    if (coords.length < 2) {
      setMapSrc(
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53902.66485719307!2d-57.589060716232815!3d-38.0032061771207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9584d94d19d34209%3A0xdd9670804bfed126!2sMar%20del%20Plata%2C%20Provincia%20de%20Buenos%20Aires!5e0!3m2!1ses-419!2sar!4v1735425233832!5m2!1ses-419!2sar"
      );
      return;
    }

    const origin = coords[0];
    const destination = coords[coords.length - 1];
    const waypoints = coords.length > 2 ? coords.slice(1, -1).join("|") : "";
    const src = `https://www.google.com/maps/embed/v1/directions?key=AIzaSyCVQ90Y-thjkWpKxI53upVe0ikMV3Gtuts&origin=${origin}&destination=${destination}${
      waypoints ? `&waypoints=${waypoints}` : ""
    }`;

    setMapSrc(src);
  }, [selectedBus]);

  useEffect(() => {
    getPaymentMethods();
  }, []);

  const handleOpenPaymentModal = (payment) => {
    setIsBusPaymentModalOpen(true);
    setPaymentToEdit(payment);
  };

  const handleCancelPaymentModal = () => {
    setIsBusPaymentModalOpen(false);
    setPaymentToEdit(null);
  };

  const handleSuccessPaymentModal = async (payment) => {
    const response = paymentToEdit
      ? await updateBusPayment(payment)
      : await createBusPayment(payment);

    // Verificar que la respuesta sea válida antes de actualizar la lista
    if (response && response.id) {
      if (paymentToEdit) {
        selectedBus.expensesIncomes = selectedBus.expensesIncomes.map((e) =>
          e.id === payment.id ? response : e
        );
      } else {
        selectedBus.expensesIncomes = [...(selectedBus.expensesIncomes || []), response];
      }
    }

    setIsBusPaymentModalOpen(false);
    setPaymentToEdit(null);

    // Refrescar el micro para obtener los datos actualizados del servidor
    updateBus();
  };

  const handleViewPassengerDetail = (passengerId) => {
    setSelectedPassengerId(passengerId);
    setIsPassengerModalOpen(true);
  };

  const handleClosePassengerModal = () => {
    setIsPassengerModalOpen(false);
    setSelectedPassengerId(null);
  };

  const handleOpenPassengerPaymentModal = (passenger, debt) => {
    setCurrentPassenger(passenger);
    setIsPaymentModalOpen(true);
    paymentForm.setFieldsValue({
      amount: debt,
      paymentMethod: null, // Autoseleccionar "Pagaron al guía"
    });
  };

  const handleClosePassengerPaymentModal = () => {
    setIsPaymentModalOpen(false);
    setCurrentPassenger(null);
    paymentForm.resetFields();
  };

  const handleCreatePassengerPayment = () => {
    paymentForm.validateFields().then((values) => {
      createPayment({
        passenger: currentPassenger.id,
        scheduledExcursionBus: selectedBus.id,
        amount: values.amount,
        user: values.paymentMethod ? user.id : selectedBus.guide.id,
        paymentMethod: values.paymentMethod,
      });

      currentPassenger.paymentAmount =
        currentPassenger.paymentAmount + values.amount;
      handleClosePassengerPaymentModal();
      updateBus();
    });
  };

  const handleOpenMovePassengerModal = (passenger) => {
    setPassengerToMove(passenger);
    setIsMovePassengerModalOpen(true);
  };

  const handleCloseMovePassengerModal = () => {
    setIsMovePassengerModalOpen(false);
    setPassengerToMove(null);
  };

  const handleMovePassengerToBus = async (targetBusId) => {
    if (!passengerToMove) return;

    await updatePassenger({
      id: passengerToMove.id,
      scheduledExcursionBus: targetBusId,
    });

    // Remover pasajero de la lista actual
    setPassengers((prev) => ({
      ...prev,
      results: passengers.results.filter((p) => p.id !== passengerToMove.id),
    }));

    handleCloseMovePassengerModal();
    updateBus();
  };

  // Obtener micros disponibles (excluyendo el actual)
  const availableBuses = selectedExcursion?.buses?.filter(
    (bus) => bus.id !== selectedBus?.id
  ) || [];

  // Filtrar y ordenar pasajeros por hotel seleccionado y zona
  const filteredPassengers = (selectedHotelFilter
    ? (passengers.results || []).filter(
        (p) => p.hotel?.id === selectedHotelFilter
      )
    : passengers.results || []
  ).sort((a, b) => getZoneIndex(a.hotel?.zones) - getZoneIndex(b.hotel?.zones));

  // Handler para seleccionar/deseleccionar hotel
  const handleHotelFilterClick = (hotelId) => {
    setSelectedHotelFilter((prev) => (prev === hotelId ? null : hotelId));
  };

  return (
    <div className="w-full">
      <Row gutter={16} className="w-full">
        {/* Pasajeros arriba */}
        <Col span={14}>
          <List
            dataSource={filteredPassengers}
            bordered
            header={
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  Pasajeros {selectedHotelFilter && `(${filteredPassengers.length})`}
                </span>
                {selectedHotelFilter && (
                  <Tag
                    color="blue"
                    closable
                    onClose={() => setSelectedHotelFilter(null)}
                    className="m-0"
                  >
                    Filtrado por hotel
                  </Tag>
                )}
              </div>
            }
            className="mb-2"
            locale={{ emptyText: "No hay usuarios disponibles" }}
            rowKey={(passenger) => passenger.id}
            renderItem={(passenger) => {
              passenger.state =
                passenger.totalAmount === passenger.paymentAmount
                  ? "PAID_COMPLETE"
                  : "OWES";

              const passengerDebt =
                passenger.totalAmount - passenger.paymentAmount >= 0
                  ? passenger.totalAmount - passenger.paymentAmount
                  : 0;

              passengerDebt === 0
                ? (passenger.state = "PAID_COMPLETE")
                : (passenger.state = "OWES");

              return (
                <List.Item className="!p-0">
                  <div className="flex items-center w-full">
                    {/* Cruz roja pequeña a la izquierda */}
                    {/* Botón mover de micro */}
                    <div
                      className="px-2 py-3 cursor-pointer hover:bg-blue-50"
                      onClick={() => handleOpenMovePassengerModal(passenger)}
                      title="Mover a otro micro"
                    >
                      <ArrowRightLeft size={14} className="text-blue-500" />
                    </div>

                    {/* Cruz roja para quitar del micro */}
                    <Popconfirm
                      title="¿Estás seguro de quitar este pasajero del micro?"
                      onConfirm={() => {
                        updatePassenger({
                          id: passenger.id,
                          scheduledExcursionBus: null,
                        });

                        setPassengers((prev) => ({
                          ...prev,
                          results: passengers.results.filter(
                            (p) => p.id !== passenger.id
                          ),
                        }));

                        updateBus();
                      }}
                      okText="Sí"
                      cancelText="No"
                    >
                      <div className="px-2 py-3 cursor-pointer hover:bg-red-50">
                        <X size={14} className="text-red-500" />
                      </div>
                    </Popconfirm>

                    {/* Celda principal clickeable para ver detalle */}
                    <div
                      className="flex-1 flex justify-between items-center py-2 px-2 cursor-pointer hover:bg-blue-50 transition-colors duration-150"
                      onClick={() => handleViewPassengerDetail(passenger.id)}
                    >
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">{`${passenger.hotel.zones} - ${passenger.hotel.name}`}</span>
                        <span className="font-medium">{passenger.name}</span>
                        <span className="font-light text-xs text-gray-400">
                          {passenger.quantitySimple}
                        </span>
                      </div>

                      <div className="flex items-center">
                        {passenger.state === "OWES" && (
                          <span className="me-2 text-red-500">{`Debe ${formatCurrency(
                            passengerDebt
                          )}`}</span>
                        )}

                        {passenger.state === "PAID_COMPLETE" && (
                          <Tag icon={<CheckSquareOutlined />} color="success">
                            Sin Deuda
                          </Tag>
                        )}
                      </div>
                    </div>

                    {/* Botón Registrar pago */}
                    {passenger.state === "OWES" && (
                      <Button
                        type="link"
                        size="small"
                        icon={<CircleDollarSign size={14} />}
                        className="text-green-600"
                        onClick={() =>
                          handleOpenPassengerPaymentModal(
                            passenger,
                            passengerDebt
                          )
                        }
                      >
                        Registrar pago
                      </Button>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />

                  </Col>

        {/* Gastos + Detalle + Hoteles + Botón mapa a la derecha */}
        <Col span={10}>
          {/* Gastos del Micro */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-md">Gastos del Micro</span>
              <Button
                type="dashed"
                size="small"
                onClick={() => handleOpenPaymentModal(null)}
              >
                + Agregar
              </Button>
            </div>
            {(selectedBus?.expensesIncomes || []).length > 0 ? (
              <div className="space-y-2">
                {(selectedBus?.expensesIncomes || []).map((expense) => (
                  <div
                    key={expense.id}
                    className="p-2 rounded-lg border bg-gray-50 border-gray-200 flex justify-between items-center"
                  >
                    <div className="flex flex-col flex-1">
                      <span className="font-medium text-sm">
                        {parseExpenseType[expense.type]}
                      </span>
                      {expense.description && (
                        <span className="text-xs text-gray-500">{expense.description}</span>
                      )}
                      {(expense.user_name || expense.userName) && (
                        <span className="text-xs text-gray-400">
                          {expense.user_name || expense.userName}
                          {(expense.payment_method_name || expense.transfer_payment_method ||
                            expense.paymentMethodName || expense.transferPaymentMethod) &&
                            ` · ${expense.payment_method_name || expense.transfer_payment_method ||
                              expense.paymentMethodName || expense.transferPaymentMethod}`}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">
                        ${expense.amount.toLocaleString()}
                      </span>
                      <EditOutlined
                        className="text-gray-400 hover:text-blue-500 cursor-pointer"
                        onClick={() => handleOpenPaymentModal(expense)}
                      />
                      <DeleteOutlined
                        className="text-gray-400 hover:text-red-500 cursor-pointer"
                        onClick={() => {
                          deleteBusPayment(expense.id);
                          selectedBus.expensesIncomes = selectedBus.expensesIncomes.filter(
                            (e) => e.id !== expense.id
                          );
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="font-light block text-gray-400 text-sm">No hay gastos registrados</span>
            )}
          </div>

          <ExcursionBusesExpandableDetail record={record} />

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-md">Hoteles</span>
              {selectedHotelFilter && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setSelectedHotelFilter(null)}
                  className="p-0 h-auto text-xs"
                >
                  Limpiar filtro
                </Button>
              )}
            </div>
            {hotelItems.length > 0 ? (
              <div className="space-y-2">
                {hotelItems.map((hotel) => (
                  <div
                    key={hotel.id}
                    onClick={() => handleHotelFilterClick(hotel.id)}
                    className={`
                      p-2 rounded-lg border cursor-pointer transition-all duration-150
                      ${selectedHotelFilter === hotel.id
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-300"
                      }
                    `}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">{hotel.zones}</span>
                        <span className={`font-medium text-sm ${selectedHotelFilter === hotel.id ? "text-blue-700" : ""}`}>
                          {hotel.name}
                        </span>
                      </div>
                      <Tag
                        color={selectedHotelFilter === hotel.id ? "blue" : "default"}
                        className="m-0"
                      >
                        {hotel.seats} pax
                      </Tag>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="font-light block mt-1 text-gray-400">No hay pasajeros</span>
            )}
          </div>

          <Button
            type="default"
            icon={<MapPin size={16} />}
            className="mt-4"
            onClick={() => setIsMapModalOpen(true)}
          >
            Ver mapa de recorrido
          </Button>
        </Col>

      </Row>

      {/* Modal del mapa */}
      <Modal
        title="Mapa de recorrido"
        open={isMapModalOpen}
        onCancel={() => setIsMapModalOpen(false)}
        footer={null}
        width={800}
      >
        <iframe
          width="100%"
          height="450"
          style={{ border: 0 }}
          src={mapSrc}
        />
      </Modal>

      <BusPaymentFormModal
        isOpen={isBusPaymentModalOpen}
        onCancel={handleCancelPaymentModal}
        paymentToEdit={paymentToEdit}
        onSuccess={handleSuccessPaymentModal}
        selectedBus={selectedBus}
      />

      <Modal
        title="Registrar pago de pasajero"
        open={isPaymentModalOpen}
        onCancel={handleClosePassengerPaymentModal}
        footer={null}
      >
        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Monto"
            rules={[{ required: true, message: "Por favor ingrese el monto" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              precision={2}
              min={0}
            />
          </Form.Item>

          <Form.Item name="paymentMethod" label="Método de pago" rules={[]}>
            <Select loading={loading.get}>
              {paymentMethods.map((method) => (
                <Select.Option key={method.id} value={method.id}>
                  {method.name} ({method.box})
                </Select.Option>
              ))}

              <Select.Option key={-1} value={null}>
                {"Pagaron al guía"} ({selectedBus.guide.firstName}{" "}
                {selectedBus.guide.lastName})
              </Select.Option>
            </Select>
          </Form.Item>

          <Button
            type="primary"
            onClick={handleCreatePassengerPayment}
            block
            loading={loading.post}
          >
            Crear pago
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Detalle de Pasajero"
        open={isPassengerModalOpen}
        onCancel={handleClosePassengerModal}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {selectedPassengerId && (
          <PassengerForm
            passengerId={selectedPassengerId}
            isModal={true}
            onSuccess={() => {
              handleClosePassengerModal();
              getPassengers({ scheduled_excursion_bus__id: selectedBus.id });
              updateBus();
            }}
          />
        )}
      </Modal>

      {/* Modal para mover pasajero a otro micro */}
      <Modal
        title={`Mover pasajero: ${passengerToMove?.name || ""}`}
        open={isMovePassengerModalOpen}
        onCancel={handleCloseMovePassengerModal}
        footer={null}
        width={500}
      >
        <p className="mb-4 text-gray-600">Selecciona el micro destino:</p>
        <List
          dataSource={availableBuses}
          locale={{ emptyText: "No hay otros micros disponibles" }}
          renderItem={(bus) => (
            <List.Item
              className="cursor-pointer hover:bg-blue-50 transition-colors duration-150"
              onClick={() => handleMovePassengerToBus(bus.id)}
            >
              <div className="flex justify-between w-full items-center">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {bus.busExcursion?.bus?.busSelectorName}
                  </span>
                  <span className="text-xs text-gray-500">
                    Guía: {bus.guide?.firstName} {bus.guide?.lastName}
                  </span>
                </div>
                <Tag color={bus.availableSeats > 0 ? "green" : "red"}>
                  {bus.availableSeats} asientos disponibles
                </Tag>
              </div>
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
}

export default ExcursionBusesExpandable;
